/**
 * The OIDC client for Cognito managed login (authorization code + PKCE), kept
 * outside React so non-component code — the callable client — can read the
 * current ID token without touching context.
 */
import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import { type RuntimeConfig, cognitoAuthority } from '../config/runtimeConfig.js';

let manager: UserManager | undefined;
let runtime: RuntimeConfig | undefined;

/** Where Cognito sends the browser back after sign-in and sign-out. */
export function appRedirectUri(): string {
  return `${window.location.origin}/`;
}

export function createUserManager(config: RuntimeConfig): UserManager {
  runtime = config;
  manager = new UserManager({
    authority: cognitoAuthority(config),
    client_id: config.userPoolClientId,
    redirect_uri: appRedirectUri(),
    post_logout_redirect_uri: appRedirectUri(),
    response_type: 'code',
    scope: 'openid email profile',
    // Renews with the refresh token shortly before the tokens expire.
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    ...(config.identityProvider
      ? { extraQueryParams: { identity_provider: config.identityProvider } }
      : {}),
  });
  // A failed renewal means the session is over: drop it so the sign-in page shows.
  manager.events.addSilentRenewError(() => {
    void manager?.removeUser();
  });
  return manager;
}

export function getUserManager(): UserManager {
  if (!manager) throw new Error('UserManager not created yet');
  return manager;
}

function isCallbackUrl(url: URL): boolean {
  return url.searchParams.has('state') && (url.searchParams.has('code') || url.searchParams.has('error'));
}

/**
 * Completes a sign-in redirect before React (and the router) mount, then puts
 * the user back on the page they started from. Returns an error message when
 * Cognito or the identity provider reported one.
 */
export async function handleSigninCallback(): Promise<string | null> {
  const url = new URL(window.location.href);
  if (!isCallbackUrl(url)) return null;
  try {
    const user = await getUserManager().signinCallback();
    const returnTo = (user?.state as { returnTo?: string } | undefined)?.returnTo ?? '/';
    window.history.replaceState({}, document.title, returnTo);
    return null;
  } catch (err) {
    window.history.replaceState({}, document.title, '/');
    return err instanceof Error ? err.message : 'Sign-in failed.';
  }
}

export async function signInRedirect(): Promise<void> {
  const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  await getUserManager().signinRedirect({ state: { returnTo } });
}

/**
 * Clears the local session, then ends the Cognito session too. Cognito has no
 * OIDC end_session_endpoint, so this is its own /logout URL.
 */
export async function signOutRedirect(): Promise<void> {
  await getUserManager().removeUser();
  if (!runtime) return;
  const params = new URLSearchParams({
    client_id: runtime.userPoolClientId,
    logout_uri: appRedirectUri(),
  });
  window.location.assign(`https://${runtime.cognitoDomain}/logout?${params.toString()}`);
}

function usableToken(user: User | null): string | null {
  if (!user || user.expired || !user.id_token) return null;
  return user.id_token;
}

/** The current ID token, renewing first if it has expired. */
export async function getIdToken(): Promise<string | null> {
  if (!manager) return null;
  const user = await manager.getUser();
  if (!user) return null;
  return usableToken(user) ?? renewIdToken();
}

/** Forces a refresh-token renewal; signs the user out locally if it fails. */
export async function renewIdToken(): Promise<string | null> {
  if (!manager) return null;
  try {
    return usableToken(await manager.signinSilent());
  } catch {
    await manager.removeUser();
    return null;
  }
}
